import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { getCampaign, updateCampaign } from "../../hook/useAdsApi";
import { AD_OBJECTIVES } from "./adsConstants";

export default function EditCampaign() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    getCampaign(campaignId)
      .then((result) => {
        setForm({
          name: result.name || "",
          objective: result.objective || "",
          budget: result.budget || "",
          budget_type: result.budget_type || "lifetime",
          start_date: result.start_date || "",
          end_date: result.end_date || "",
        });
      })
      .catch(() => toast.error("Failed to load campaign", { toastId: "edit-campaign-load-error" }))
      .finally(() => setLoading(false));
  }, [campaignId]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const canSave = form?.name.trim() && form?.objective && form?.budget !== "" && form?.start_date && form?.end_date;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCampaign(campaignId, {
        name: form.name,
        objective: form.objective,
        budget: Number(form.budget),
        start_date: form.start_date,
        end_date: form.end_date,
      });
      toast.success("Campaign updated");
      navigate(`/ads/campaigns/${campaignId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
              <h1 className="text-xl font-bold">Edit Campaign</h1>
            </div>

            {loading || !form ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : (
              <>
                <label className="block text-sm font-semibold text-black mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => updateForm({ name: e.target.value })}
                  className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-6"
                />

                <label className="block text-sm font-semibold text-black mb-2">Objective</label>
                <div className="flex flex-wrap gap-2 mb-6">
                  {AD_OBJECTIVES.map((obj) => (
                    <button
                      key={obj.value}
                      type="button"
                      onClick={() => updateForm({ objective: obj.value })}
                      className={`px-4 py-2 rounded-full text-sm font-semibold ${
                        form.objective === obj.value
                          ? "bg-[#095228] text-white"
                          : "border border-[#c3c5d9] text-[#434656]"
                      }`}
                    >
                      {obj.label}
                    </button>
                  ))}
                </div>

                <label className="block text-sm font-semibold text-black mb-1">
                  Budget ({form.budget_type === "daily" ? "Daily" : "Lifetime"})
                </label>
                <input
                  type="number"
                  value={form.budget}
                  onChange={(e) => updateForm({ budget: e.target.value })}
                  className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-1"
                />
                {/* Budget type itself cannot be changed after creation - client-api explicitly
                    rejects it ("Campaign budget type cannot be changed"), so it's shown as a
                    fixed label rather than an editable dropdown. */}
                <p className="text-xs text-[#9a9a9a] mb-6">Budget type can't be changed after creation.</p>

                <div className="flex gap-3 mb-8">
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-black mb-1">Start Date</label>
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => updateForm({ start_date: e.target.value })}
                      className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-semibold text-black mb-1">End Date</label>
                    <input
                      type="date"
                      value={form.end_date}
                      min={form.start_date || undefined}
                      onChange={(e) => updateForm({ end_date: e.target.value })}
                      className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!canSave || saving}
                  onClick={handleSave}
                  className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
